/**
 * The floating bubble as a Document Picture-in-Picture window (Chrome / Edge): no address bar, no minimise button,
 * always on top. The window only hosts an iframe of the normal detached-chat route (/chat-window), so the chat code,
 * the one-owner lock and the cross-tab messages are exactly the pop-up's. It lives as long as the tab that opened it.
 */

/** PiP's own minimum is about 262 x 106 outside; this is the pill the admin approved, ~260 x 100. */
export const PIP_PILL = { w: 260, h: 100 };

interface PipApi {
  requestWindow(options: {
    width: number;
    height: number;
    disallowReturnToOpener?: boolean;
    preferInitialWindowPlacement?: boolean;
  }): Promise<Window>;
}

function pipApi(): PipApi | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as { documentPictureInPicture?: PipApi }).documentPictureInPicture ?? null;
}

/** Document PiP exists here, and this window can ask for it (only a top-level page can). */
export function canUsePip(): boolean {
  try {
    return !!pipApi() && window.self === window.top;
  } catch {
    return false;
  }
}

/** Where the PiP window goes so the bubble is under the pointer; PiP's bar is above the content. */
export function pipOriginAtPointer(sx: number, sy: number, barH: number): { x: number; y: number } {
  return { x: Math.round(sx - PIP_PILL.w / 2), y: Math.round(sy - barH - PIP_PILL.h / 2) };
}

/** Opens the PiP window showing `url`; null when the browser refuses (the caller falls back to a pop-up). */
export async function openChatPip(url: string, pointer: { x: number; y: number } | null): Promise<Window | null> {
  const api = pipApi();
  if (!api) return null;
  try {
    // preferInitialWindowPlacement: Chrome otherwise reopens PiP at the size it last had on this site (a whole chat).
    const pip = await api.requestWindow({
      width: PIP_PILL.w,
      height: PIP_PILL.h,
      disallowReturnToOpener: true,
      preferInitialWindowPlacement: true,
    });
    // Not the inside size asked for (older Chrome ignores that option, or counts the bar in): correct it now, by the
    // difference, while the click still counts as the user's.
    const dw = PIP_PILL.w - pip.innerWidth;
    const dh = PIP_PILL.h - pip.innerHeight;
    if (Math.abs(dw) > 4 || Math.abs(dh) > 4) {
      try {
        pip.resizeBy(dw, dh);
      } catch {
        /* refused: it stays at the browser's size */
      }
    }
    pip.document.title = 'Job chat';
    const body = pip.document.body;
    body.style.margin = '0';
    body.style.overflow = 'hidden';
    const frame = pip.document.createElement('iframe');
    frame.src = url;
    frame.setAttribute('allow', 'autoplay');
    frame.style.cssText = 'border:0;width:100vw;height:100vh;display:block';
    body.appendChild(frame);
    if (pointer) {
      try {
        const at = pipOriginAtPointer(pointer.x, pointer.y, pip.outerHeight - pip.innerHeight);
        pip.moveTo(at.x, at.y);
      } catch {
        /* the browser keeps its own place */
      }
    }
    return pip;
  } catch {
    return null;
  }
}
